import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  Phone,
  Mail,
  DollarSign,
  Calendar,
  Percent,
  AlertCircle,
  Lightbulb,
  Loader2,
  RefreshCw,
  Download,
  Building2,
  TrendingUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import BankComparison from "../components/BankComparison";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [showCallbackDialog, setShowCallbackDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [leadForm, setLeadForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_time: "",
    notes: ""
  });

  useEffect(() => {
    const storedResult = sessionStorage.getItem("qualificationResult");
    if (storedResult) {
      const parsed = JSON.parse(storedResult);
      setResult(parsed);
      setLeadForm({
        ...leadForm,
        full_name: parsed.applicant_name || "",
        email: parsed.applicant_email || ""
      });
    } else {
      navigate("/qualify");
    }
  }, [navigate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleLeadSubmit = async (type) => {
    if (!leadForm.full_name || !leadForm.email) {
      toast.error("Please fill in your name and email.");
      return;
    }

    setIsSubmittingLead(true);
    try {
      await axios.post(`${API}/leads`, {
        application_id: result.id,
        request_type: type,
        full_name: leadForm.full_name,
        email: leadForm.email,
        phone: leadForm.phone || null,
        preferred_time: leadForm.preferred_time || null,
        notes: leadForm.notes || null
      });

      toast.success(
        type === "callback" 
          ? "Callback request submitted! We'll be in touch soon."
          : "Your results have been saved and will be emailed to you."
      );
      
      setShowCallbackDialog(false);
      setShowEmailDialog(false);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const handleSendEmail = async () => {
    if (!leadForm.email) {
      toast.error("Please enter your email address.");
      return;
    }

    setIsSendingEmail(true);
    try {
      await axios.post(`${API}/email/send-results`, {
        application_id: result.id,
        recipient_email: leadForm.email
      });
      toast.success(`Results sent to ${leadForm.email}`);
      setShowEmailDialog(false);
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error("Email service is not configured. Your results have been saved.");
        // Fall back to saving info
        await handleLeadSubmit("email_results");
      } else {
        toast.error("Failed to send email. Please try again.");
      }
    } finally {
      setIsSendingEmail(false);
    }
  };

  const generatePDF = async () => {
    setIsGeneratingPdf(true);
    try {
      const doc = new jsPDF();
      const isQualified = result.status === "qualified";
      const matchedBanks = result.matched_banks || [];

      // Header
      doc.setFillColor(91, 181, 162);
      doc.rect(0, 0, 220, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("Financify", 20, 25);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text("Loan Pre-Qualification Results", 20, 35);

      // Status Badge
      const statusY = 55;
      if (isQualified) {
        doc.setFillColor(232, 184, 74);
        doc.roundedRect(70, statusY - 8, 70, 16, 8, 8, 'F');
        doc.setTextColor(15, 61, 62);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("You Pre-Qualify!", 105, statusY + 3, { align: "center" });
      } else {
        doc.setFillColor(239, 68, 68);
        doc.roundedRect(55, statusY - 8, 100, 16, 8, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Not Eligible At This Time", 105, statusY + 3, { align: "center" });
      }

      // Applicant Info
      doc.setTextColor(15, 61, 62);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(`Hello, ${result.applicant_name}`, 20, 85);

      // Summary Box
      doc.setFillColor(248, 249, 250);
      doc.roundedRect(15, 95, 180, 50, 5, 5, 'F');
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);

      const summaryData = [
        ["Requested Amount:", formatCurrency(result.requested_amount)],
        ["Total Monthly Income:", formatCurrency(result.total_monthly_income)],
        ["Total Monthly Debt:", formatCurrency(result.total_monthly_debt)],
        ["Current TDSR:", `${result.tdsr_percentage?.toFixed(1) || 0}%`]
      ];

      let summaryY = 108;
      summaryData.forEach(([label, value]) => {
        doc.setFont("helvetica", "normal");
        doc.text(label, 25, summaryY);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 61, 62);
        doc.text(value, 100, summaryY);
        doc.setTextColor(100, 100, 100);
        summaryY += 10;
      });

      // Matched Banks Table
      if (matchedBanks.length > 0) {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 61, 62);
        doc.text("Matched Banks", 20, 160);

        autoTable(doc, {
          startY: 165,
          head: [['Bank', 'Amount', 'Monthly Payment', 'Term', 'Rate', 'TDSR']],
          body: matchedBanks.map(bank => [
            bank.bank_name,
            formatCurrency(bank.max_approved_amount),
            `${formatCurrency(bank.estimated_monthly_payment)}/mo`,
            `${bank.max_term_years} years`,
            `${bank.interest_rate}%`,
            `${bank.tdsr_percentage?.toFixed(1) || 0}%`
          ]),
          theme: 'striped',
          headStyles: { fillColor: [91, 181, 162], textColor: [255, 255, 255] },
          styles: { fontSize: 9, cellPadding: 4 },
          margin: { left: 15, right: 15 }
        });
      }

      // Decline Reasons (if not qualified)
      if (!isQualified && result.decline_reasons?.length > 0) {
        const reasonsY = matchedBanks.length > 0 ? doc.lastAutoTable.finalY + 15 : 160;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(239, 68, 68);
        doc.text("Reasons:", 20, reasonsY);
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        let reasonY = reasonsY + 8;
        result.decline_reasons.forEach((reason, i) => {
          doc.text(`${i + 1}. ${reason}`, 25, reasonY);
          reasonY += 7;
        });
      }

      // Suggestions (if not qualified)
      if (!isQualified && result.suggestions?.length > 0) {
        const suggestY = doc.lastAutoTable?.finalY + 50 || 200;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(232, 184, 74);
        doc.text("Suggestions to Improve:", 20, suggestY);
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        let sugY = suggestY + 8;
        result.suggestions.forEach((sug, i) => {
          doc.text(`${i + 1}. ${sug}`, 25, sugY);
          sugY += 7;
        });
      }

      // Disclaimer
      const disclaimerY = 265;
      doc.setFillColor(255, 243, 205);
      doc.roundedRect(15, disclaimerY, 180, 20, 3, 3, 'F');
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 80, 20);
      doc.text(
        "Important: This is a pre-qualification only. Final approval is subject to bank verification.",
        20, disclaimerY + 8
      );
      doc.text(
        `Application ID: ${result.id}`,
        20, disclaimerY + 14
      );

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 290);
      doc.text("© 2024 Financify - Bahamas Loan Pre-Qualification Platform", 105, 290, { align: "center" });

      // Save
      doc.save(`Financify_Results_${result.id.slice(0, 8)}.pdf`);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#52BFB8]" />
      </div>
    );
  }

  const isQualified = result.status === "qualified";
  const matchedBanks = result.matched_banks || [];

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 md:py-12">
      <div className="container-main">
        {/* Status Header */}
        <div className={`max-w-3xl mx-auto rounded-3xl p-8 md:p-12 mb-8 ${
          isQualified ? "result-card-qualified celebrate" : "result-card-not-qualified"
        }`} data-testid="results-card">
          <div className="text-center">
            {isQualified ? (
              <>
                <div className="w-20 h-20 bg-[#E8B84A] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-white" />
                </div>
                <div className="badge-success inline-block mb-4">
                  You Pre-Qualify!
                </div>
                <h1 className="heading-2 mb-2">
                  Congratulations, {result.applicant_name.split(' ')[0]}!
                </h1>
                <p className="text-lg text-gray-600">
                  Based on the information provided, you pre-qualify for loans from {matchedBanks.length} bank{matchedBanks.length !== 1 ? 's' : ''}.
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-gray-500" />
                </div>
                <div className="badge-error inline-block mb-4">
                  Not Eligible At This Time
                </div>
                <h1 className="heading-2 mb-2">
                  We couldn't find a match right now
                </h1>
                <p className="text-lg text-gray-600">
                  Don't worry—there are steps you can take to improve your eligibility.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Qualified Results - Show All Banks */}
        {isQualified && matchedBanks.length > 0 && (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold text-[#4A4A4A] mb-4">Your Matched Banks</h2>
            
            {/* Bank Cards */}
            <div className="space-y-4 mb-8">
              {matchedBanks.map((bank, index) => (
                <div 
                  key={index} 
                  className={`card-base stagger-item ${index === 0 ? 'ring-2 ring-[#E8B84A]' : ''}`}
                  data-testid={`bank-card-${index}`}
                >
                  {index === 0 && (
                    <div className="inline-block bg-[#E8B84A] text-white text-xs px-3 py-1 rounded-full mb-4 font-medium">
                      Best Match
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 mb-6">
                    {bank.bank_logo ? (
                      <img 
                        src={bank.bank_logo} 
                        alt={bank.bank_name}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-[#4A4A4A] text-lg">{bank.bank_name}</p>
                      <p className="text-sm text-gray-500">Interest Rate: {bank.interest_rate}% p.a.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-xs">Loan Amount</span>
                      </div>
                      <p className="text-xl font-bold text-[#4A4A4A]">
                        {formatCurrency(bank.max_approved_amount)}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Calendar className="w-4 h-4" />
                        <span className="text-xs">Monthly</span>
                      </div>
                      <p className="text-xl font-bold text-[#52BFB8]">
                        {formatCurrency(bank.estimated_monthly_payment)}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Calendar className="w-4 h-4" />
                        <span className="text-xs">Term</span>
                      </div>
                      <p className="text-xl font-bold text-[#4A4A4A]">
                        {bank.max_term_years} years
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Percent className="w-4 h-4" />
                        <span className="text-xs">TDSR</span>
                      </div>
                      <p className="text-xl font-bold text-[#4A4A4A]">
                        {bank.tdsr_percentage?.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  {bank.max_approved_amount < result.requested_amount && (
                    <p className="text-xs text-amber-600 mt-3 bg-amber-50 p-2 rounded-lg">
                      Note: Loan amount adjusted from {formatCurrency(result.requested_amount)} to fit eligibility criteria
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Bank Comparison Table */}
            {matchedBanks.length > 1 && (
              <BankComparison banks={matchedBanks} requestedAmount={result.requested_amount} />
            )}

            {/* CTA Buttons */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8 mt-8 stagger-item">
              <Dialog open={showCallbackDialog} onOpenChange={setShowCallbackDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary h-14" data-testid="btn-callback">
                    <Phone className="w-5 h-5 mr-2" />
                    Request Callback
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Request a Callback</DialogTitle>
                    <DialogDescription>
                      We'll connect you with a loan specialist who can help you proceed.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="cb-name">Full Name</Label>
                      <Input
                        id="cb-name"
                        value={leadForm.full_name}
                        onChange={(e) => setLeadForm({...leadForm, full_name: e.target.value})}
                        className="mt-1"
                        data-testid="callback-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cb-phone">Phone Number</Label>
                      <Input
                        id="cb-phone"
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                        className="mt-1"
                        data-testid="callback-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cb-time">Preferred Time (optional)</Label>
                      <Input
                        id="cb-time"
                        placeholder="e.g. Afternoons, Weekends"
                        value={leadForm.preferred_time}
                        onChange={(e) => setLeadForm({...leadForm, preferred_time: e.target.value})}
                        className="mt-1"
                        data-testid="callback-time"
                      />
                    </div>
                    <Button 
                      className="w-full btn-primary" 
                      onClick={() => handleLeadSubmit("callback")}
                      disabled={isSubmittingLead}
                      data-testid="submit-callback"
                    >
                      {isSubmittingLead ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="btn-secondary h-14" data-testid="btn-email">
                    <Mail className="w-5 h-5 mr-2" />
                    Email Results
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Email Your Results</DialogTitle>
                    <DialogDescription>
                      We'll send a summary of your pre-qualification to your email.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="em-name">Full Name</Label>
                      <Input
                        id="em-name"
                        value={leadForm.full_name}
                        onChange={(e) => setLeadForm({...leadForm, full_name: e.target.value})}
                        className="mt-1"
                        data-testid="email-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="em-email">Email Address</Label>
                      <Input
                        id="em-email"
                        type="email"
                        value={leadForm.email}
                        onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                        className="mt-1"
                        data-testid="email-address"
                      />
                    </div>
                    <Button 
                      className="w-full btn-primary" 
                      onClick={handleSendEmail}
                      disabled={isSendingEmail}
                      data-testid="submit-email"
                    >
                      {isSendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Results"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button 
                variant="outline" 
                className="btn-secondary h-14" 
                onClick={generatePDF}
                disabled={isGeneratingPdf}
                data-testid="btn-pdf"
              >
                {isGeneratingPdf ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Download PDF
              </Button>
            </div>
          </div>
        )}

        {/* Not Qualified - Reasons & Suggestions */}
        {!isQualified && (
          <div className="max-w-3xl mx-auto">
            {/* Decline Reasons */}
            {result.decline_reasons?.length > 0 && (
              <div className="card-base mb-6 stagger-item" data-testid="decline-reasons">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                  <h3 className="text-lg font-semibold text-[#4A4A4A]">Why you didn't qualify</h3>
                </div>
                <ul className="space-y-3">
                  {result.decline_reasons.map((reason, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-600">
                      <span className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                        {index + 1}
                      </span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suggestions */}
            {result.suggestions?.length > 0 && (
              <div className="card-base mb-6 stagger-item" data-testid="suggestions">
                <div className="flex items-center gap-3 mb-4">
                  <Lightbulb className="w-6 h-6 text-[#E8B84A]" />
                  <h3 className="text-lg font-semibold text-[#4A4A4A]">How to improve your chances</h3>
                </div>
                <ul className="space-y-3">
                  {result.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-600">
                      <span className="w-6 h-6 bg-[#E8B84A]/20 text-[#E8B84A] rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                        {index + 1}
                      </span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Application Summary */}
            <div className="card-base mb-6 stagger-item" data-testid="application-summary">
              <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4">Your Application Summary</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Requested Amount</p>
                  <p className="font-semibold">{formatCurrency(result.requested_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Monthly Income</p>
                  <p className="font-semibold">{formatCurrency(result.total_monthly_income)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Monthly Debt</p>
                  <p className="font-semibold">{formatCurrency(result.total_monthly_debt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Current TDSR</p>
                  <p className="font-semibold">{result.tdsr_percentage}%</p>
                </div>
              </div>
            </div>

            {/* CTAs for Not Qualified */}
            <div className="grid sm:grid-cols-3 gap-4 stagger-item">
              <Link to="/qualify" data-testid="btn-try-again">
                <Button className="btn-primary w-full h-14">
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Try Again
                </Button>
              </Link>
              
              <Button 
                variant="outline" 
                className="btn-secondary h-14" 
                onClick={generatePDF}
                disabled={isGeneratingPdf}
                data-testid="btn-pdf-declined"
              >
                {isGeneratingPdf ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Download PDF
              </Button>
              
              <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="btn-secondary h-14" data-testid="btn-save-info">
                    <Mail className="w-5 h-5 mr-2" />
                    Save Info
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Save Your Information</DialogTitle>
                    <DialogDescription>
                      We'll notify you when you may be eligible or if new options become available.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="save-name">Full Name</Label>
                      <Input
                        id="save-name"
                        value={leadForm.full_name}
                        onChange={(e) => setLeadForm({...leadForm, full_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="save-email">Email Address</Label>
                      <Input
                        id="save-email"
                        type="email"
                        value={leadForm.email}
                        onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <Button 
                      className="w-full btn-primary" 
                      onClick={() => handleLeadSubmit("save_info")}
                      disabled={isSubmittingLead}
                    >
                      {isSubmittingLead ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save My Info"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        )}

        {/* Sign Up Prompt for Non-Logged-In Users */}
        {!user && (
          <div className="max-w-3xl mx-auto mt-8">
            <div className="card-base bg-[#52BFB8]/5 border-[#52BFB8]/20">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-[#4A4A4A]">Track Your Applications</h3>
                  <p className="text-sm text-gray-600">Create an account to save and track all your loan applications.</p>
                </div>
                <Link to="/login" data-testid="signup-prompt">
                  <Button className="btn-primary whitespace-nowrap">
                    Sign Up Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="max-w-3xl mx-auto mt-12 text-center">
          <p className="text-sm text-gray-500">
            <strong>Important:</strong> This is a pre-qualification only and does not guarantee loan approval. 
            Final approval is subject to verification of information provided and bank-specific requirements.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Application ID: {result.id}
          </p>
          <Link 
            to="/disclaimer" 
            className="text-sm text-[#52BFB8] hover:underline mt-2 inline-block"
            data-testid="link-disclaimer"
          >
            View full disclaimer
          </Link>
        </div>
      </div>
    </div>
  );
}
